import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  Pagination,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import axios from 'axios';
import { Special } from '../types';
import config from '../config';

const ITEMS_PER_PAGE = 20;

const SpecialsManager: React.FC = () => {
  const [specials, setSpecials] = useState<Special[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpecial, setEditingSpecial] = useState<Special | null>(null);
  const [name, setName] = useState('');
  const [date, setDate] = useState<Date | null>(new Date());

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [specialToDelete, setSpecialToDelete] = useState<Special | null>(null);

  const fetchSpecials = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get<Special[]>(`${config.api.baseUrl}/specials`, {
        withCredentials: true,
      });
      setSpecials(response.data);
    } catch (err) {
      console.error('Error fetching specials:', err);
      setError('Failed to fetch special days');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecials();
  }, []);

  const totalPages = Math.max(1, Math.ceil(specials.length / ITEMS_PER_PAGE));
  const paginatedSpecials = specials.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [specials.length, page, totalPages]);

  const handleOpenDialog = (special?: Special) => {
    if (special) {
      setEditingSpecial(special);
      setName(special.name);
      setDate(new Date(special.date));
    } else {
      setEditingSpecial(null);
      setName('');
      setDate(new Date());
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSpecial(null);
    setName('');
    setDate(new Date());
  };

  const handleSave = async () => {
    if (!name || !date) return;

    const payload = {
      name,
      date: date.toISOString(),
      // template is intentionally omitted from UI; backend will accept null
    };

    try {
      setLoading(true);
      setError(null);
      if (editingSpecial) {
        await axios.put(
          `${config.api.baseUrl}/specials/${editingSpecial.id}`,
          payload,
          { withCredentials: true },
        );
      } else {
        await axios.post(
          `${config.api.baseUrl}/specials`,
          payload,
          { withCredentials: true },
        );
      }
      await fetchSpecials();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving special:', err);
      setError('Failed to save special day');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = (special: Special) => {
    setSpecialToDelete(special);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!specialToDelete) return;
    try {
      setLoading(true);
      setError(null);
      await axios.delete(
        `${config.api.baseUrl}/specials/${specialToDelete.id}`,
        { withCredentials: true },
      );
      await fetchSpecials();
      setDeleteConfirmOpen(false);
      setSpecialToDelete(null);
    } catch (err) {
      console.error('Error deleting special:', err);
      setError('Failed to delete special day');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4">Special Days</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Special
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSpecials.map((special) => (
                <TableRow key={special.id}>
                  <TableCell>{special.name}</TableCell>
                  <TableCell>
                    {special.date
                      ? format(new Date(special.date), 'EEE MMM d, yyyy p')
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(special)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleConfirmDelete(special)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {specials.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No special days configured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingSpecial ? 'Edit Special Day' : 'Add Special Day'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <DatePicker
              label="Date"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  required: true,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!name || !date || loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Special Day</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{specialToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecialsManager;

